/**
 * Defensive credential redaction for Sentry events, breadcrumbs and transactions.
 *
 * Why: the email signup confirmation link lands on `/#access_token=…&refresh_token=…`, and Sentry
 * records page URLs (request.url, navigation breadcrumbs, transaction names, stack abs_path). Those
 * are live credentials, so every string in a payload is scrubbed before it leaves the browser.
 * Narrow on purpose: it only rewrites credential-shaped values; consent gating and init options
 * live in main.tsx and are untouched.
 */
const CREDENTIAL_KEYS =
  /^(access_token|refresh_token|provider_token|provider_refresh_token|id_token)$/;
const CREDENTIAL_PARAM =
  /\b(access_token|refresh_token|provider_token|provider_refresh_token|id_token|code)=[^&#\s"'<>]*/g;
const JWT_LIKE = /\beyJ[\w-]{5,}\.[\w-]{5,}\.[\w-]*/g;
const REDACTED = '[redacted]';
const MAX_DEPTH = 12;

function scrubString(value: string): string {
  // URLSearchParams accepts encoded names. Decode valid runs separately so malformed,
  // unrelated percent text cannot prevent scrubbing an otherwise valid credential key.
  const decoded = value.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
    try {
      return decodeURIComponent(run);
    } catch {
      return run;
    }
  });
  return decoded.replace(CREDENTIAL_PARAM, '$1=' + REDACTED).replace(JWT_LIKE, REDACTED);
}

function scrub(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH || seen.has(value)) return undefined;
  seen.add(value);
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1, seen));
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    out[key] = CREDENTIAL_KEYS.test(key) ? REDACTED : scrub(v, depth + 1, seen);
  }
  return out;
}

/** Returns a scrubbed copy; the input is never mutated. Use for beforeSend / beforeBreadcrumb / beforeSendTransaction. */
export function scrubSentryPayload<T>(payload: T): T {
  return scrub(payload, 0, new WeakSet()) as T;
}
